import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, ChevronDown, Loader2, Mail, Eye, Pencil } from 'lucide-react';
import OpsModal from '@/components/ops/OpsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

const DATE_STATUS_OPTS = [
  { value: 1, label: 'Date confirmed',                              help: 'Interview date is locked in.' },
  { value: 2, label: 'Date pencilled in, awaiting consent',         help: "Date proposed but loved one hasn't confirmed yet." },
  { value: 3, label: 'No date yet — discussing with loved one',     help: 'Still talking to the loved one about whether and when.' },
];

export default function EmailComposeModal({ leadId, template, onClose, onSent }) {
  const templateKey = template?.key || 'post_discovery_followup';
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [subjectIndex, setSubjectIndex] = useState(0);
  const [dateStatus, setDateStatus] = useState(null); // null = use server default
  const [toggleB, setToggleB] = useState(false);
  const [editedBody, setEditedBody] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showSubjectPicker, setShowSubjectPicker] = useState(false);

  const fetchPreview = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/ops/leads/${leadId}/email/preview`, {
        template_key: templateKey,
        subject_index: subjectIndex,
        date_status: dateStatus,
        toggle_b: toggleB,
        body_override: isEditing ? editedBody : null,
      }, getAuth());
      setPreview(data);
      if (dateStatus === null && data.date_status_default) setDateStatus(data.date_status_default);
      if (!isEditing) setEditedBody(data.body);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [leadId, templateKey, subjectIndex, dateStatus, toggleB, isEditing, editedBody]);

  useEffect(() => { fetchPreview(); }, [templateKey, subjectIndex, dateStatus, toggleB]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPreview(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = async () => {
    if (!preview?.recipient_email) { toast.error('No recipient email on this lead'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/ops/leads/${leadId}/email/send`, {
        subject: preview.subject,
        body: isEditing ? editedBody : preview.body,
        recipient_email: preview.recipient_email,
        template_key: templateKey,
        date_status: dateStatus,
        toggle_b: toggleB,
      }, getAuth());
      toast.success('Email sent successfully');
      onSent(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (loading && !preview) {
    return (
      <div className="ops-shell fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center">
        <div className="w-7 h-7 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  const isAdvanced = !!preview?.has_advanced_logic;

  const footer = (
    <div className="flex items-center justify-between gap-3">
      <button
        onClick={onClose}
        className="text-[13px] text-slate-600 hover:text-slate-900 transition-colors"
        data-testid="cancel-send-email"
      >Cancel</button>
      <button
        onClick={handleSend}
        disabled={sending || !preview?.recipient_email}
        data-testid="send-email-button"
        className="h-9 inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-4 rounded-md text-[13px] font-medium shadow-sm disabled:opacity-50 transition-colors"
      >
        {sending
          ? <><Loader2 size={13} className="animate-spin" /> Sending…</>
          : <><Send size={13} /> Send email</>}
      </button>
    </div>
  );

  return (
    <OpsModal
      title={preview?.template_name || 'Compose email'}
      subtitle={isAdvanced ? 'Advanced mode · conditional content' : 'Simple mode'}
      icon={Mail}
      onClose={onClose}
      size={isAdvanced ? 'xl' : 'lg'}
      zClass="z-[60]"
      footer={footer}
      testid="email-compose-modal"
    >
      <div className={`grid ${isAdvanced ? 'lg:grid-cols-[360px_1fr]' : 'grid-cols-1'} divide-y lg:divide-y-0 lg:divide-x divide-slate-200`}>

        {/* LEFT pane — settings */}
        <div className="px-5 py-4 space-y-4 lg:max-h-[68vh] lg:overflow-y-auto">

          {/* To / From */}
          <div className="grid grid-cols-1 gap-3 text-[13px]">
            <div>
              <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-0.5">To</p>
              <p className="text-slate-900">{preview?.recipient_email || '—'}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-0.5">From</p>
              <p className="text-slate-900">{preview?.from_email}</p>
              {preview?.reply_to && (
                <p className="text-[11px] text-slate-500">Reply-to: {preview.reply_to}</p>
              )}
            </div>
          </div>

          {/* Subject picker */}
          <div>
            <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1.5">Subject line</p>
            <div className="relative">
              <button
                onClick={() => setShowSubjectPicker(v => !v)}
                data-testid="subject-picker"
                className="w-full text-left h-9 bg-white border border-slate-200 focus:border-slate-900 hover:border-slate-300 rounded-md px-2.5 text-[13px] text-slate-900 flex items-center justify-between transition-colors"
              >
                <span className="truncate">{preview?.subject || '…'}</span>
                <ChevronDown size={13} className="text-slate-400 shrink-0 ml-2" />
              </button>
              {showSubjectPicker && preview?.subjects && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-md z-10 max-h-72 overflow-y-auto py-1">
                  {preview.subjects.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => { setSubjectIndex(i); setShowSubjectPicker(false); }}
                      data-testid={`subject-option-${i}`}
                      className={`w-full text-left px-3 py-2 text-[13px] hover:bg-slate-50 transition-colors ${i === subjectIndex ? 'bg-slate-50 text-slate-900 font-medium' : 'text-slate-700'}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {preview?.subjects?.length > 1 && (
              <p className="text-[11px] text-slate-500 mt-1">{preview.subjects.length} variants available</p>
            )}
          </div>

          {/* Advanced controls */}
          {isAdvanced && (
            <>
              <div className="space-y-2" data-testid="date-status-group">
                <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500">Interview date status</p>
                <div className="space-y-1.5">
                  {DATE_STATUS_OPTS.map(opt => {
                    const checked = dateStatus === opt.value;
                    return (
                      <label
                        key={opt.value}
                        data-testid={`date-status-${opt.value}`}
                        className={`flex items-start gap-2.5 cursor-pointer rounded-md border px-2.5 py-2 transition-colors ${checked ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                      >
                        <input
                          type="radio"
                          name="date_status"
                          checked={checked}
                          onChange={() => setDateStatus(opt.value)}
                          className="mt-1 accent-slate-900"
                        />
                        <span className="min-w-0">
                          <span className="text-[13px] text-slate-900 block leading-tight">{opt.label}</span>
                          <span className="text-[11px] text-slate-500 block mt-0.5 leading-snug">{opt.help}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div data-testid="toggle-b">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <button
                    type="button"
                    onClick={() => setToggleB(v => !v)}
                    className={`relative w-9 h-5 rounded-full transition-colors mt-0.5 shrink-0 ${toggleB ? 'bg-slate-900' : 'bg-slate-300'}`}
                    aria-pressed={toggleB}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${toggleB ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </button>
                  <span>
                    <span className="text-[13px] text-slate-900 block leading-tight">Consent not yet confirmed</span>
                    <span className="text-[11px] text-slate-500 block mt-0.5 leading-snug">Adds the consent-pending paragraph for the loved one.</span>
                  </span>
                </label>
              </div>
            </>
          )}

          {/* Merge fields reference */}
          {preview?.merge_fields && (
            <details className="text-[12px]">
              <summary className="text-[11px] uppercase tracking-wider font-medium text-slate-500 cursor-pointer hover:text-slate-900 list-none flex items-center gap-1">
                <ChevronDown size={11} className="transition-transform [details[open]_&]:rotate-180" />
                Merge field values
              </summary>
              <div className="mt-2 grid grid-cols-1 gap-1">
                {Object.entries(preview.merge_fields).map(([k, v]) => (
                  <div key={k} className="text-[11px] text-slate-600">
                    <code className="text-slate-400">{`{${k}}`}</code> = {v || <span className="italic text-slate-400">empty</span>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* RIGHT pane — live preview / editor */}
        <div className="px-5 py-4 lg:max-h-[68vh] lg:overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 inline-flex items-center gap-1.5">
              {isEditing ? <><Pencil size={11} /> Editing body</> : <><Eye size={11} /> Live preview</>}
            </p>
            <button
              onClick={() => setIsEditing(v => !v)}
              data-testid="toggle-edit-body"
              className="h-7 text-[12px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md px-2.5 transition-colors"
            >
              {isEditing ? 'Show preview' : 'Edit text'}
            </button>
          </div>
          {isEditing ? (
            <textarea
              value={editedBody}
              onChange={e => setEditedBody(e.target.value)}
              data-testid="email-body-editor"
              className="w-full bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-3 py-2.5 text-[13px] text-slate-900 outline-none resize-none leading-relaxed transition-colors"
              rows={18}
            />
          ) : (
            <div
              data-testid="email-preview"
              className="bg-white border border-slate-200 rounded-md px-5 py-5 shadow-sm"
            >
              <p className="text-[13px] font-medium text-slate-900 pb-3 mb-3 border-b border-slate-100">
                {preview?.subject || ''}
              </p>
              <div className="text-[13px] text-slate-800 leading-relaxed whitespace-pre-line">
                {isEditing ? editedBody : preview?.body}
              </div>
            </div>
          )}
        </div>
      </div>
    </OpsModal>
  );
}
